import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Vector3, Euler } from 'three';
import { useParticipant, useAppMessage, useAudioTrack, useVideoTrack, useMeetingState } from '@daily-co/daily-react';
import { SetUserPositionAndRotationWorldMessage, WorldMessage } from '../../../models/Types';
import { DailyEventObjectAppMessage } from '@daily-co/daily-js';
import { createAppMessage } from '../../../utils/call';

import _ from 'lodash';
import UserAvatar from './UserAvatar';
import { UserAudio } from '../../../models/UserAudio';
import { AudioConfig } from '../../../utils/audio-config';

const DailyUserAvatar: React.FC<{ userId: string, audioConfig: AudioConfig }> = ({ userId, audioConfig }) => {
  const participant = useParticipant(userId);
  const dailyAudioTrack = useAudioTrack(userId);
  const [userAudio, setUserAudio] = useState<UserAudio>();
  const dailyVideoTrack = useVideoTrack(userId);
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack>();

  const prevVideoRef = useRef<MediaStreamTrack>();
  const prevUserAudioRef = useRef<UserAudio>();

  const [position, setPosition] = useState<Vector3>(new Vector3(0,1,0));
  const [rotation, setRotation] = useState<Euler>(new Euler(0,0,0));
  const sendAppMessage = useAppMessage();

  const updateUserAudio = (updatedUserAudio: UserAudio | undefined) => {
    setUserAudio((prevUserAudio: UserAudio | undefined) => {
      if (prevUserAudio) {
        prevUserAudioRef.current = prevUserAudio;
        // prevUserAudio.sound.disconnect();
      }
      return updatedUserAudio;
    })
  }

  useEffect(() => {
    console.log(dailyAudioTrack);
    if (dailyAudioTrack.state !== 'playable') return updateUserAudio(undefined); // console.info('audio track state is currently', dailyAudioTrack.state);
    if (!dailyAudioTrack.persistentTrack || dailyAudioTrack.persistentTrack.readyState === 'ended') return updateUserAudio(undefined); // console.info('no audio track at the moment');
    if (participant?.local) return updateUserAudio(undefined); // console.info('local user, not creating audio track');

    updateUserAudio(new UserAudio(dailyAudioTrack.persistentTrack));

    return () => {
      if (userAudio) userAudio.sound.disconnect();
    }
  }, [dailyAudioTrack.state, dailyAudioTrack.persistentTrack?.readyState]);

  useEffect(() => {
    userAudio?.updateAudioConfig(audioConfig);
  }, [userAudio, audioConfig])

  useEffect(() => {
    if (prevUserAudioRef.current) prevUserAudioRef.current.sound.disconnect();
  }, [userAudio]);

  useEffect(() => {
    if (prevVideoRef.current) prevVideoRef.current.stop();
  }, [videoTrack]);

  useEffect(() => {
    console.log(dailyVideoTrack, dailyVideoTrack.state === 'playable' && dailyVideoTrack.persistentTrack?.readyState === 'live');
    if (dailyVideoTrack.state === 'playable' && dailyVideoTrack.persistentTrack?.readyState === 'live') {
      setVideoTrack(prev => {
        prevVideoRef.current = prev;
        return dailyVideoTrack.persistentTrack;
      })
    } 
  }, [dailyVideoTrack.state, dailyVideoTrack.persistentTrack?.readyState]);

  useEffect(() => {
    const getInitialStateMessage: WorldMessage = {
      messageType: 'world',
      worldMessageType: 'get-initial-state'
    };
    sendAppMessage(getInitialStateMessage);
  }, []);

  useAppMessage({
    onAppMessage: useCallback((message: DailyEventObjectAppMessage) => {
      if (message.fromId !== userId) return;
      const appMessage = createAppMessage(message);
      if (appMessage.message.messageType === 'world' && (appMessage.message.worldMessageType === 'set-user-position-and-rotation')) {
        const userPositionAndRotationMessage = appMessage.message as SetUserPositionAndRotationWorldMessage;
        const updatedPosition = new Vector3(userPositionAndRotationMessage.position.x, userPositionAndRotationMessage.position.y, userPositionAndRotationMessage.position.z);
        const updatedRotation = new Euler(userPositionAndRotationMessage.headRotation.x, userPositionAndRotationMessage.headRotation.y, userPositionAndRotationMessage.headRotation.z);
        // rotation-z: (0.3 -> going left) -- (-0.3 -> going right) -- rotation-x (0.3 -> going forward, -0.3 -> going back)
        setPosition(updatedPosition);
        setRotation(updatedRotation);
      }
    }, []),
  });

  return (
    <UserAvatar userName={participant?.user_name ?? 'anon'} position={position} rotation={rotation} userAudio={userAudio} videoTrackOrSrc={videoTrack} />
  );
};

export default DailyUserAvatar;
